import React, { useEffect, useState, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useUser } from "../store/UserProvider";
import { ROUTES } from "./routeNames";
import { useLogin } from "../hooks/useLogin";
import { LOCAL_STORAGE_TOKEN_KEY } from "../services/api/config";
import { CompassParagraph } from "../components/common/styledComponents";
import { GlobalSearchPalette } from "../components/common/GlobalSearchPalette";
import { UserRole, CognitoClaim } from "../models/userModels";
import { useToast } from "../store/ToastProvider";
import { MESSAGES } from "../messages";

const checkForTokenInLocalStorage = (): string | null => {
  return localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
};

const handleAuthError = (errMsg: string): void => {
  console.error(errMsg);
};

// Routes that upload-manager role can access
const UPLOAD_MANAGER_ALLOWED_ROUTES = [
  ROUTES.ADD_FILE,
  ROUTES.ADD_FILE_DEFINITION,
];

/**
 * Checks if the current path is allowed for an upload manager
 */
const isRouteAllowedForUploadManager = (pathname: string): boolean => {
  return UPLOAD_MANAGER_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(ROUTES.ADD_FILE + "/"),
  );
};

export const ProtectedRoute: React.FC = () => {
  const { user } = useUser();
  const { handleLogin } = useLogin();
  const { addToast } = useToast();
  const location = useLocation();

  // Track if we're in the middle of verifying with the token
  const [loading, setLoading] = useState(true);
  // Track if we need to redirect due to role restriction
  const [shouldRedirectUploadManager, setShouldRedirectUploadManager] =
    useState(false);
  // Prevent showing toast multiple times
  const hasShownToast = useRef(false);

  useEffect(() => {
    // If user is not authenticated, try logging in with any existing token
    if (!user?.isAuthenticated) {
      const token = checkForTokenInLocalStorage();
      if (token) {
        (async (): Promise<void> => {
          try {
            await handleLogin("", "", handleAuthError, token);
          } catch (err) {
            console.error("Token login failed:", err);
          } finally {
            setLoading(false);
          }
        })();
      } else {
        // No token, no user => definitely not authenticated
        setLoading(false);
      }
    } else {
      // User is already authenticated
      setLoading(false);
    }
  }, [user?.isAuthenticated, handleLogin]);

  // Check role-based access for upload managers
  useEffect(() => {
    if (!loading && user?.isAuthenticated) {
      const userRole = user[CognitoClaim.ROLE];
      if (
        userRole === UserRole.MANAGER_UPLOAD &&
        !isRouteAllowedForUploadManager(location.pathname)
      ) {
        if (!hasShownToast.current) {
          addToast(MESSAGES.MAGIC_LINK.ACCESS_RESTRICTED, "info");
          hasShownToast.current = true;
        }
        setShouldRedirectUploadManager(true);
      } else {
        setShouldRedirectUploadManager(false);
        hasShownToast.current = false;
      }
    }
  }, [loading, user, location.pathname, addToast]);

  // While verifying with the token, show a loading indicator
  if (loading) {
    return <CompassParagraph>Loading...</CompassParagraph>;
  }

  // If still not authenticated, go to login
  if (!user?.isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Redirect upload managers to allowed route
  if (shouldRedirectUploadManager) {
    return <Navigate to={ROUTES.ADD_FILE} replace />;
  }

  // Otherwise, render the protected outlet
  return (
    <>
      <Outlet />
      <GlobalSearchPalette />
    </>
  );
};
